
// TODO: THIS IS BETTER IMPLEMENTED AS JUST AN EXPOSED UTIL FUNCTION 
// THAT CAN BE CONSUMED BY USERS IN A FUNCTION NODE



/**
 * ArrayMathNode - Element-wise arithmetic on an array
 *
 * Applies op(input[i], operand[i]) or op(input[i], scalar) for each element.
 * If `operand` is connected, uses element-wise operation; otherwise uses scalar.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const arrayMathEndpoints = {
  ...params({
    op:     { dataType: 'string', value: 'add', options: ['add', 'subtract', 'multiply', 'divide'] },
    scalar: { value: 0, min: -10000, max: 10000, step: 0.1 },
  }),
  ...internals({
    input:   { dataType: 'array', isInput: true, label: 'Input' },
    operand: { dataType: 'array', isInput: true, label: 'Operand' },
    output:  { dataType: 'array', isOutput: true, label: 'Output' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class ArrayMathNode extends BaseNode<typeof arrayMathEndpoints> {
  private cachedInput: number[] | null = null;
  private cachedOperand: number[] | null = null;

  onInternalChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'input') this.cachedInput = _payload.value as number[];
    else if (endpointId === 'operand') this.cachedOperand = _payload.value as number[];
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    if (!this.cachedInput) return;

    const input = this.cachedInput;
    const op = this.params.op.value as string;
    const scalar = this.params.scalar.value;
    const operand = this.cachedOperand;

    const result: number[] = [];
    for (let i = 0; i < input.length; i++) {
      const b = operand ? (operand[Math.min(i, operand.length - 1)] ?? scalar) : scalar;
      switch (op) {
        case 'add':      result.push(input[i] + b); break;
        case 'subtract': result.push(input[i] - b); break;
        case 'multiply': result.push(input[i] * b); break;
        case 'divide':   result.push(b !== 0 ? input[i] / b : 0); break;
        default:         result.push(input[i]); break;
      }
    }

    this.internals.output.set(result);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const arrayMathNodeDef: NodeDef<ArrayMathNode> = {
  type: 'ArrayMath',
  shortName: 'math',
  info: 'Element-wise arithmetic on an array. Uses a scalar or a second array as the operand.',
  domain: Domain.Data,
  category: 'Array',
  endpoints: arrayMathEndpoints,
  ctor: ArrayMathNode,
};
